import React, { useState, useEffect } from 'react';
import { Viewer, SpecialZoomLevel } from '@react-pdf-viewer/core';
import {
  defaultLayoutPlugin,
  ToolbarProps,
  ThumbnailIcon,
} from '@react-pdf-viewer/default-layout';
import { thumbnailPlugin } from '@react-pdf-viewer/thumbnail';
import { GlobalWorkerOptions } from 'pdfjs-dist';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import '@react-pdf-viewer/thumbnail/lib/styles/index.css';
import { DarkIcon, LightIcon } from '@react-pdf-viewer/theme';
import './ConsentPDFViewer.scss';

GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.4.120/pdf.worker.min.js`;

interface Props {
  metadata: any;
}

export const ConsentPDFViewer = ({ metadata }: Props) => {
  const [containerDimensions, setContainerDimensions] = useState({
    width: 600,
    height: 750,
  });
  const [theme, setTheme] = useState<'light' | 'dark'>('light');

  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  useEffect(() => {
    const updateDimensions = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;

      if (width <= 768) {
        setContainerDimensions({
          width: Math.min(width * 0.95, 600),
          height: Math.min(height * 0.7, 600),
        });
      } else {
        setContainerDimensions({
          width: 600,
          height: 750,
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);

    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Create thumbnail plugin instance
  const thumbnailPluginInstance = thumbnailPlugin();
  const { Thumbnails } = thumbnailPluginInstance;

  // Create default layout plugin with custom toolbar
  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    sidebarTabs: () => [
      {
        icon: <ThumbnailIcon />,
        content: <Thumbnails />,
        title: 'Thumbnails',
      },
    ],
    renderToolbar: (Toolbar: React.FC<ToolbarProps>) => (
      <Toolbar>
        {slots => {
          const {
            CurrentPageInput,
            Download,
            GoToNextPage,
            GoToPreviousPage,
            NumberOfPages,
            Zoom,
            ZoomIn,
            ZoomOut,
          } = slots;

          return (
            <div
              className={`rpv-toolbar rpv-toolbar--${theme}`}
              role="toolbar"
              aria-orientation="horizontal"
            >
              <div className="rpv-toolbar__left">
                <div className="rpv-toolbar__item">
                  <ZoomOut />
                </div>
                <div className="rpv-toolbar__item">
                  <Zoom />
                </div>
                <div className="rpv-toolbar__item">
                  <ZoomIn />
                </div>
              </div>

              <div className="rpv-toolbar__center">
                <div className="rpv-toolbar__item">
                  <GoToPreviousPage />
                </div>
                <div className="rpv-toolbar__item rpv-toolbar__page-number">
                  <CurrentPageInput /> / <NumberOfPages />
                </div>
                <div className="rpv-toolbar__item">
                  <GoToNextPage />
                </div>
              </div>

              <div className="rpv-toolbar__right">
                <div className="rpv-toolbar__item mr-3" onClick={toggleTheme}>
                  {theme === 'light' ? <LightIcon /> : <DarkIcon />}
                </div>
                <div className="rpv-toolbar__item">
                  <Download />
                </div>
              </div>
            </div>
          );
        }}
      </Toolbar>
    ),
  });

  return (
    <div className="pdf-viewer-container d-flex justify-content-center align-item-center">
      <div
        className="pdf-container"
        style={{
          height: `${containerDimensions.height}px`,
          width: `${containerDimensions.width}px`,
        }}
      >
        <Viewer
          fileUrl={metadata.signed_url}
          plugins={[defaultLayoutPluginInstance, thumbnailPluginInstance]}
          defaultScale={
            window.innerWidth <= 768
              ? SpecialZoomLevel.PageWidth
              : SpecialZoomLevel.PageFit
          }
          theme={theme}
        />
      </div>
    </div>
  );
};
